// ADM-1 (2026-06-12) — Reembolsos (web), funcionando IGUAL ao PWA campo.
// Mesma infra: endpoints /mobile/reembolsos/* da mobile-engine, mesmas
// tabelas mobile.reembolsos_pendentes, mesmo OCR (Bedrock) e mesmo workflow
// de aprovação do financeiro (FinanceiroReembolsosCampo).
// Fluxo com comprovante: upload-url → PUT S3 → processar (OCR) → conferir/
// editar (PATCH). Sem comprovante: form manual com justificativa.

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Alert, Button, Card, DatePicker, Drawer, Empty, Flex, Form,
  Input, InputNumber, Select, Space, Spin, Steps, Table, Tag, Typography,
} from 'antd';
import {
  CameraOutlined, FileExclamationOutlined, ReloadOutlined, EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { fmtDataBr } from '../utils/dataFmt';

const { Text, Title } = Typography;
const { TextArea } = Input;

const STATUS_CFG: Record<string, { label: string; cor: string }> = {
  aguardando_upload:  { label: 'Enviando…',            cor: 'default' },
  aguardando_ocr:     { label: 'Processando',          cor: 'processing' },
  aguardando_revisao: { label: 'Aguardando aprovação', cor: 'gold' },
  aprovado:           { label: 'Aprovado ✓',           cor: 'success' },
  rejeitado:          { label: 'Rejeitado',            cor: 'error' },
  duplicado:          { label: 'Possível duplicado',   cor: 'warning' },
  erro_ocr:           { label: 'Erro na leitura',      cor: 'error' },
};

interface Reembolso {
  id: number;
  status: string;
  ai_extracao: any;
  contrato_id: string | null;
  contrato_numero: string | null;
  centro_custo_codigo: string | null;
  centro_custo_nome: string | null;
  sem_comprovante: boolean;
  motivo_rejeicao: string | null;
  comentario_financeiro: string | null;
  created_at: string;
}

const destinoPayload = (chave: string | null) => ({
  contrato_id: chave?.startsWith('contrato:') ? chave.slice(9) : null,
  centro_custo_codigo: chave?.startsWith('centro:') ? chave.slice(7) : null,
});

function AtividadesReembolsosInner() {
  const { message } = AntApp.useApp();
  const [lista, setLista] = useState<Reembolso[]>([]);
  const [loading, setLoading] = useState(true);
  const [contratos, setContratos] = useState<Array<{ id: string; numero: string; titulo: string }>>([]);
  const [centrosCusto, setCentrosCusto] = useState<Array<{ codigo_canonical: string; nome_completo: string }>>([]);
  const [novoAberto, setNovoAberto] = useState<'comprovante' | 'manual' | null>(null);
  const [detalhe, setDetalhe] = useState<any | null>(null);
  // fluxo com comprovante
  const [passo, setPasso] = useState(0);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [destinoSel, setDestinoSel] = useState<string | null>(null);
  const [descricaoSolic, setDescricaoSolic] = useState<string>('');
  const [processando, setProcessando] = useState(false);
  const [reembolsoId, setReembolsoId] = useState<number | null>(null);
  const [extracao, setExtracao] = useState<any | null>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [formEdicao] = Form.useForm();
  const [formManual] = Form.useForm();
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/mobile/reembolsos`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setLista(Array.isArray(d) ? d : (d.reembolsos || []));
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  // Deep-link do sino: quando o aviso aponta pra um reembolso específico, abre o
  // detalhe assim que a lista carrega (uma vez só).
  const deepLinkFeito = useRef(false);
  useEffect(() => {
    if (deepLinkFeito.current || !lista.length) return;
    const alvo = sessionStorage.getItem('reembolsos_abrir_id');
    if (!alvo) return;
    sessionStorage.removeItem('reembolsos_abrir_id');
    deepLinkFeito.current = true;
    const r = lista.find((x) => String(x.id) === alvo);
    if (r) abrirDetalhe(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lista]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/mobile/contratos`, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setContratos(Array.isArray(d) ? d : (d.contratos || []));
        setCentrosCusto(Array.isArray(d?.centros_custo) ? d.centros_custo : []);
      } catch { /* opcional */ }
    })();
  }, []);

  function resetFluxo() {
    setPasso(0); setArquivo(null); setDestinoSel(null); setDescricaoSolic('');
    setReembolsoId(null); setExtracao(null);
    formEdicao.resetFields(); formManual.resetFields();
  }

  // ─── Fluxo COM comprovante (igual PWA: upload → OCR → conferir) ───────────
  async function enviarComprovante() {
    if (!arquivo) { message.warning('Selecione a foto/print do comprovante'); return; }
    setProcessando(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      // 1) cria o reembolso + presigned URL
      const r1 = await axios.post(`${API_URL}/mobile/reembolsos/upload-url`,
        { content_type: arquivo.type || 'image/jpeg', ...destinoPayload(destinoSel),
          // A que o gasto se refere, na palavra de quem pede (o comprovante
          // diz a loja; só quem gastou sabe a finalidade).
          descricao_solicitante: (descricaoSolic || '').trim() || null },
        { headers });
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      setReembolsoId(d1.reembolso_id);
      // 2) PUT direto no S3
      await axios.put(d1.upload_url, arquivo,
        { headers: { 'Content-Type': arquivo.type || 'image/jpeg' }, timeout: 120000 });
      setPasso(1);
      // 3) OCR (Bedrock — 10~30s)
      const r3 = await axios.post(`${API_URL}/mobile/reembolsos/${d1.reembolso_id}/processar`,
        {}, { headers, timeout: 90000 });
      const d3 = r3.data.body ? JSON.parse(r3.data.body) : r3.data;
      setExtracao(d3.extracao || {});
      formEdicao.setFieldsValue({
        valor: d3.extracao?.valor,
        data_emissao: d3.extracao?.data_emissao ? dayjs(d3.extracao.data_emissao) : null,
        emissor: d3.extracao?.emissor,
        // O que a pessoa escreveu ganha da leitura do comprovante — ela pode
        // ajustar aqui, mas não perde o que digitou.
        descricao: (descricaoSolic || '').trim() || d3.extracao?.descricao,
      });
      if (d3.duplicado) message.warning('Atenção: possível duplicado de outro comprovante já enviado.');
      setPasso(2);
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
      setPasso(0);
    } finally { setProcessando(false); }
  }

  async function confirmarEdicao(values: any) {
    if (!reembolsoId) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.patch(`${API_URL}/mobile/reembolsos/${reembolsoId}`,
        { valor: values.valor,
          data_emissao: values.data_emissao ? values.data_emissao.format('YYYY-MM-DD') : undefined,
          emissor: values.emissor || undefined,
          descricao: values.descricao || undefined,
        }, { headers: { Authorization: token } });
      message.success('Reembolso enviado pro financeiro!');
      setNovoAberto(null);
      resetFluxo();
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  // ─── Fluxo SEM comprovante ────────────────────────────────────────────────
  async function enviarManual(values: any) {
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/mobile/reembolsos/sem-comprovante`,
        { valor: values.valor,
          data_emissao: values.data_emissao.format('YYYY-MM-DD'),
          emissor: values.emissor || null,
          descricao: values.descricao || null,
          justificativa: values.justificativa,
          ...destinoPayload(values.destino || null),
        }, { headers: { Authorization: token } });
      message.success('Reembolso enviado pro financeiro!');
      setNovoAberto(null);
      resetFluxo();
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function abrirDetalhe(r: Reembolso) {
    try {
      const token = await getToken();
      const resp = await axios.get(`${API_URL}/mobile/reembolsos/${r.id}`,
        { headers: { Authorization: token } });
      const d = resp.data.body ? JSON.parse(resp.data.body) : resp.data;
      setDetalhe(d);
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  const colunas = [
    {
      title: 'Valor', key: 'valor', width: 110,
      render: (_: any, r: Reembolso) => {
        const v = parseFloat(r.ai_extracao?.valor);
        return <Text strong style={{ fontSize: 12 }}>
          {isNaN(v) ? '—' : `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
        </Text>;
      },
    },
    {
      title: 'Fornecedor / descrição', key: 'forn', ellipsis: true,
      render: (_: any, r: Reembolso) => (
        <div>
          <Text style={{ fontSize: 12 }}>{r.ai_extracao?.emissor || r.ai_extracao?.fornecedor || r.ai_extracao?.descricao || '—'}</Text>
          {r.sem_comprovante && <Tag style={{ fontSize: 9, marginLeft: 6 }}>sem comprovante</Tag>}
        </div>
      ),
    },
    {
      title: 'Centro de custo', key: 'destino', width: 160,
      render: (_: any, r: Reembolso) => r.contrato_numero || r.centro_custo_nome
        ? <Tag style={{ fontSize: 10 }}>{r.contrato_numero || r.centro_custo_nome}</Tag>
        : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
    },
    {
      title: 'Data', key: 'data', width: 100,
      render: (_: any, r: Reembolso) => (
        <Text style={{ fontSize: 11 }}>
          {r.ai_extracao?.data_emissao ? dayjs(r.ai_extracao.data_emissao).format('DD/MM/YYYY') : fmtDataBr(r.created_at)}
        </Text>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 160,
      render: (s: string, r: Reembolso) => (
        <div>
          <Tag color={STATUS_CFG[s]?.cor}>{STATUS_CFG[s]?.label || s}</Tag>
          {r.motivo_rejeicao && s === 'rejeitado' && (
            <Text type="danger" style={{ fontSize: 10, display: 'block' }}>{r.motivo_rejeicao}</Text>
          )}
        </div>
      ),
    },
    {
      title: '', key: 'a', width: 50,
      render: (_: any, r: Reembolso) => (
        <Button size="small" type="text" icon={<EyeOutlined />}
          onClick={(e) => { e.stopPropagation(); abrirDetalhe(r); }} />
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>💸 Reembolsos</Title>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={carregar} />
          <Button icon={<FileExclamationOutlined />} onClick={() => { resetFluxo(); setNovoAberto('manual'); }}>
            Sem comprovante
          </Button>
          <Button type="primary" icon={<CameraOutlined />} onClick={() => { resetFluxo(); setNovoAberto('comprovante'); }}>
            Novo reembolso
          </Button>
        </Space>
      </Flex>
      <Text type="secondary" style={{ display: 'block', marginBottom: 14, fontSize: 12 }}>
        Mesmo fluxo do app de campo: foto/print do comprovante, leitura automática dos
        dados e aprovação pelo financeiro. Acompanhe o status de cada pedido abaixo.
      </Text>

      <Table size="small" rowKey="id" columns={colunas as any} dataSource={lista}
        loading={loading} pagination={{ pageSize: 20 }}
        onRow={(r) => ({ onClick: () => abrirDetalhe(r), style: { cursor: 'pointer' } })}
        locale={{ emptyText: <Empty description="Nenhum reembolso ainda" /> }} />

      {/* ─── Drawer: novo com comprovante ─── */}
      <Drawer open={novoAberto === 'comprovante'}
        onClose={() => { if (!processando) { setNovoAberto(null); resetFluxo(); } }}
        title="Novo reembolso (com comprovante)" width={520} destroyOnClose>
        <Steps size="small" current={passo} style={{ marginBottom: 18 }}
          items={[{ title: 'Comprovante' }, { title: 'Leitura' }, { title: 'Conferir' }]} />

        {passo === 0 && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Select placeholder="Centro de custo (opcional)" allowClear
              value={destinoSel} onChange={setDestinoSel}
              showSearch optionFilterProp="label" style={{ width: '100%' }}
              options={[
                { label: 'Contratos', options: contratos.map(c => ({ value: `contrato:${c.id}`, label: `${c.numero} — ${c.titulo}` })) },
                { label: 'Centros indiretos', options: centrosCusto.map(c => ({ value: `centro:${c.codigo_canonical}`, label: `${c.nome_completo} (${c.codigo_canonical})` })) },
              ]} />
            <div>
              <Input.TextArea rows={2} maxLength={500} value={descricaoSolic}
                onChange={(e) => setDescricaoSolic(e.target.value)}
                placeholder="A que se refere? Ex.: almoço da equipe na campanha do Taquari" />
              <Text type="secondary" style={{ fontSize: 11 }}>
                O comprovante mostra a loja e o valor — escreva aqui pra que serviu.
              </Text>
            </div>
            <Button icon={<CameraOutlined />} block size="large"
              onClick={() => inputFileRef.current?.click()}>
              {arquivo ? `📎 ${arquivo.name}` : 'Selecionar foto/print do comprovante'}
            </Button>
            <input ref={inputFileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setArquivo(f); e.target.value = ''; }} />
            <Button type="primary" block loading={processando}
              disabled={!arquivo} onClick={enviarComprovante}>
              Enviar e ler comprovante
            </Button>
          </Space>
        )}

        {passo === 1 && (
          <Flex vertical align="center" style={{ padding: 30 }}>
            <Spin size="large" />
            <Text type="secondary" style={{ marginTop: 12, fontSize: 12 }}>
              Lendo o comprovante com IA… (10-30s)
            </Text>
          </Flex>
        )}

        {passo === 2 && (
          <>
            {extracao?.confianca != null && extracao.confianca < 0.6 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message="Leitura com baixa confiança — confira os campos abaixo" />
            )}
            <Form form={formEdicao} layout="vertical" size="small" onFinish={confirmarEdicao}>
              <Flex gap={12}>
                <Form.Item label="Valor (R$)" name="valor" style={{ flex: 1 }}
                  rules={[{ required: true, message: 'Valor obrigatório' }]}>
                  <InputNumber min={0.01} step={0.01} decimalSeparator="," style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label="Data" name="data_emissao" style={{ flex: 1 }}
                  rules={[{ required: true, message: 'Data obrigatória' }]}>
                  <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Flex>
              <Form.Item label="Emissor (loja)" name="emissor">
                <Input />
              </Form.Item>
              <Form.Item label="Descrição" name="descricao">
                <Input placeholder="Ex: almoço equipe campo, pedágio…" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={salvando} block>
                Confirmar e enviar pro financeiro
              </Button>
            </Form>
          </>
        )}
      </Drawer>

      {/* ─── Drawer: sem comprovante ─── */}
      <Drawer open={novoAberto === 'manual'}
        onClose={() => { setNovoAberto(null); resetFluxo(); }}
        title="Reembolso sem comprovante" width={520} destroyOnClose>
        <Alert type="warning" showIcon style={{ marginBottom: 14 }}
          message="Sem comprovante a justificativa é obrigatória"
          description="Explique por que não há comprovante (ex: máquina de estacionamento sem recibo)." />
        <Form form={formManual} layout="vertical" size="small" onFinish={enviarManual}>
          <Flex gap={12}>
            <Form.Item label="Valor (R$)" name="valor" style={{ flex: 1 }}
              rules={[{ required: true, message: 'Valor obrigatório' }]}>
              <InputNumber min={0.01} step={0.01} decimalSeparator="," style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Data" name="data_emissao" style={{ flex: 1 }}
              rules={[{ required: true, message: 'Data obrigatória' }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Flex>
          <Form.Item label="Emissor (loja)" name="emissor">
            <Input />
          </Form.Item>
          <Form.Item label="Descrição" name="descricao">
            <Input />
          </Form.Item>
          <Form.Item label="Centro de custo (opcional)" name="destino">
            <Select allowClear showSearch optionFilterProp="label"
              options={[
                { label: 'Contratos', options: contratos.map(c => ({ value: `contrato:${c.id}`, label: `${c.numero} — ${c.titulo}` })) },
                { label: 'Centros indiretos', options: centrosCusto.map(c => ({ value: `centro:${c.codigo_canonical}`, label: `${c.nome_completo} (${c.codigo_canonical})` })) },
              ]} />
          </Form.Item>
          <Form.Item label="Justificativa (por que não tem comprovante?)" name="justificativa"
            rules={[{ required: true, min: 10, message: 'Mínimo 10 caracteres' }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={salvando} block>
            Enviar pro financeiro
          </Button>
        </Form>
      </Drawer>

      {/* ─── Drawer: detalhe ─── */}
      <Drawer open={!!detalhe} onClose={() => setDetalhe(null)}
        title={detalhe ? `Reembolso #${detalhe.reembolso?.id}` : ''} width={520}>
        {detalhe?.reembolso && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space size={8} wrap>
              <Tag color={STATUS_CFG[detalhe.reembolso.status]?.cor}>
                {STATUS_CFG[detalhe.reembolso.status]?.label || detalhe.reembolso.status}
              </Tag>
              {(detalhe.reembolso.contrato_numero || detalhe.reembolso.centro_custo_nome)
                && <Tag>{detalhe.reembolso.contrato_numero || detalhe.reembolso.centro_custo_nome}</Tag>}
            </Space>
            <Card size="small" bodyStyle={{ padding: 10 }}>
              <Space direction="vertical" size={4}>
                <Text style={{ fontSize: 12 }}><strong>Valor:</strong> R$ {parseFloat(detalhe.reembolso.ai_extracao?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Text>
                <Text style={{ fontSize: 12 }}><strong>Data:</strong> {detalhe.reembolso.ai_extracao?.data_emissao ? dayjs(detalhe.reembolso.ai_extracao.data_emissao).format('DD/MM/YYYY') : '—'}</Text>
                <Text style={{ fontSize: 12 }}><strong>Emissor:</strong> {detalhe.reembolso.ai_extracao?.emissor || detalhe.reembolso.ai_extracao?.fornecedor || '—'}</Text>
                {detalhe.reembolso.ai_extracao?.descricao && (
                  <Text style={{ fontSize: 12 }}><strong>Descrição:</strong> {detalhe.reembolso.ai_extracao.descricao}</Text>
                )}
                {detalhe.reembolso.comentario_financeiro && (
                  <Text style={{ fontSize: 12 }}><strong>Financeiro:</strong> {detalhe.reembolso.comentario_financeiro}</Text>
                )}
                {detalhe.reembolso.motivo_rejeicao && (
                  <Text type="danger" style={{ fontSize: 12 }}><strong>Motivo da rejeição:</strong> {detalhe.reembolso.motivo_rejeicao}</Text>
                )}
              </Space>
            </Card>
            {detalhe.comprovante_url && !detalhe.reembolso.sem_comprovante && (
              /* PDF não renderiza em <img> — mesma correção da tela do financeiro */
              /pdf/i.test(detalhe.reembolso.comprovante_content_type || '')
              || /\.pdf$/i.test(detalhe.reembolso.comprovante_s3_key || '') ? (
                <>
                  <iframe src={detalhe.comprovante_url} title="Comprovante em PDF"
                    style={{ width: '100%', height: 420, borderRadius: 6, border: '1px solid #eee', background: '#fafafa' }} />
                  <Button size="small" style={{ marginTop: 8 }}
                    onClick={() => window.open(detalhe.comprovante_url, '_blank', 'noopener')}>
                    Abrir PDF em nova aba
                  </Button>
                </>
              ) : (
                <img src={detalhe.comprovante_url} alt="Comprovante"
                  style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid #eee' }} />
              )
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
}

export default function AtividadesReembolsos() {
  return <AntApp><AtividadesReembolsosInner /></AntApp>;
}
